import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  entityRouteRef,
  useAsyncEntity,
  useEntityPresentation,
} from '@backstage/plugin-catalog-react';
import { useApi, useRouteRefParams } from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import type { EntityHeaderLayoutProps } from '@backstage/plugin-catalog-react/alpha';
import {
  AccountEntity,
  getAccountSyncState,
} from '@internal/backstage-plugin-catalog-devops-common';
import { convertDateFormat } from '@internal/backstage-plugin-devops-react';
import { EntityChip } from './EntityChip';
import { BreadcrumbTrail } from './AccountPageHeader';
import { HeaderTabs } from './HeaderTabs';
import styles from './ProjectPageHeader.module.css';

/**
 * Project (Domain) header, 1:1 with project-overview-page-mockup.html:
 * Domain badge, live "N accounts · AWS + Azure" chip, owner chip, sync
 * badge (worst state across the accounts) and the facts subtitle. Scans
 * run per account — no scan/settings actions here by design.
 */
const ProjectPageHeader = ({ tabs, activeTabId }: EntityHeaderLayoutProps) => {
  const { entity } = useAsyncEntity();
  const routeParams = useRouteRefParams(entityRouteRef);
  const presentation = useEntityPresentation(entity ?? routeParams);
  const catalogApi = useApi(catalogApiRef);

  const [accounts, setAccounts] = useState<AccountEntity[]>([]);
  const [resourceCount, setResourceCount] = useState<number | undefined>(
    undefined,
  );

  useEffect(() => {
    let cancelled = false;
    catalogApi
      .getEntities({ filter: { kind: 'Account', 'spec.domain': entity?.metadata.name ?? '' } })
      .then(({ items }) => {
        if (!cancelled) {
          setAccounts(items as AccountEntity[]);
        }
      })
      .catch(() => {
        // The header chips simply stay generic.
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, entity?.metadata.name]);

  useEffect(() => {
    let cancelled = false;
    if (accounts.length === 0) {
      return () => {};
    }
    catalogApi
      .getEntities({
        filter: accounts.map(account => ({
          kind: 'CloudResource',
          'spec.account': account.metadata.name,
        })),
      })
      .then(({ items }) => {
        if (!cancelled) {
          setResourceCount(items.length);
        }
      })
      .catch(() => {
        // The subtitle degrades to accounts/regions only.
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, accounts]);

  const providers = useMemo(
    () =>
      [
        ...new Set(
          accounts.map(account => (account.spec.type ?? '').toUpperCase()),
        ),
      ].filter(Boolean),
    [accounts],
  );

  const withErrors = accounts.filter(
    account => getAccountSyncState(account).errorCount > 0,
  );
  const everSynced = accounts.filter(account => account.spec.lastScanTime);
  const allSynced = accounts.length > 0 && withErrors.length === 0 && everSynced.length === accounts.length;
  const lastScan = accounts
    .map(account => account.spec.lastScanTime)
    .filter(Boolean)
    .sort()
    .pop();
  const regions = useMemo(
    () => [
      ...new Set(
        accounts.flatMap(account =>
          (account.spec.regions ?? []).map(region => region.Name),
        ),
      ),
    ],
    [accounts],
  );

  const breadcrumbs = useMemo(() => {
    const trail: { label: string; href: string }[] = [];
    if (entity) {
      trail.push({
        label: presentation.primaryTitle,
        href: `/catalog/default/domain/${entity.metadata.name}`,
      });
    }
    return trail;
  }, [entity, presentation.primaryTitle]);

  const subtitleSegments: ReactNode[] = [];
  if (accounts.length > 0) {
    subtitleSegments.push(
      `Project over ${accounts.length} ${
        accounts.length === 1 ? 'account' : 'accounts'
      } across ${providers.join(' and ') || '—'}`,
    );
  }
  if (resourceCount !== undefined) {
    subtitleSegments.push(`${resourceCount} resources`);
  }
  if (regions.length > 0) {
    const shown = regions.slice(0, 3).join(', ');
    subtitleSegments.push(
      regions.length > 3 ? `${shown} + ${regions.length - 3}` : shown,
    );
  }
  const lastScanLabel = convertDateFormat(lastScan);
  if (lastScanLabel) {
    subtitleSegments.push(`last scan ${lastScanLabel}`);
  }

  return (
    <BreadcrumbTrail entries={breadcrumbs}>
      <div className={styles.root}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>{presentation.primaryTitle}</h1>
          {entity && <EntityChip tone="info">{entity.kind}</EntityChip>}
          {accounts.length > 0 && (
            <EntityChip tone="neutral">
              {`${accounts.length} account${accounts.length > 1 ? 's' : ''} · ${providers.join(' + ') || '—'}`}
            </EntityChip>
          )}
          {entity?.spec?.owner && (
            <EntityChip tone="neutral">
              {`Owner: ${String(entity.spec.owner)
                .split(':')
                .pop()
                ?.split('/')
                .pop()}`}
            </EntityChip>
          )}
          {accounts.length > 0 && withErrors.length > 0 && (
            <EntityChip tone="warning">
              {`● ${withErrors.length} with sync errors`}
            </EntityChip>
          )}
          {accounts.length > 0 && withErrors.length === 0 && allSynced && (
            <EntityChip tone="success">● All accounts synced</EntityChip>
          )}
        </div>
        {subtitleSegments.length > 0 && (
          <p className={styles.subtitle}>
            {subtitleSegments.map((segment, index) => (
              <Fragment key={index}>
                {index > 0 && ' · '}
                {segment}
              </Fragment>
            ))}
          </p>
        )}
        <HeaderTabs tabs={tabs} activeTabId={activeTabId} />
      </div>
    </BreadcrumbTrail>
  );
};

export { ProjectPageHeader };
